import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import type { Member } from '@/types';
import { roleLabels, roleColors } from '@/mocks/organization';
import { api } from '@/api/client';

const ORG_ID = () => localStorage.getItem('hr_org_id');
import MemberActionsMenu from './components/MemberActionsMenu';
import SetPasswordModal from './components/SetPasswordModal';
import EditRoleModal from './components/EditRoleModal';
import DeleteMemberModal from './components/DeleteMemberModal';

const ALL_ROLES = ['all', 'org_admin', 'hr_manager', 'recruiter', 'viewer'] as const;
const ALL_STATUSES = ['all', 'active', 'pending'] as const;

type RoleFilter = (typeof ALL_ROLES)[number];
type StatusFilter = (typeof ALL_STATUSES)[number];

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error';
}

function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = (message: string, type: Toast['type'] = 'success') => {
    const id = `t${Date.now()}`;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  };

  return { toasts, show };
}

export default function Members() {
  const { orgId } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);

  interface ApiMember { id: string; user_id?: string; name?: string; email?: string; user_name?: string; user_email?: string; role: string; created_at?: string }
  const loadMembers = () => {
    const orgId = ORG_ID();
    if (!orgId) return;
    api.get<ApiMember[]>(`/organizations/${orgId}/members`).then((rows) => {
      setMembers(rows.map((r) => ({
        id: r.id,
        name: r.user_name || r.name || (r.user_email || r.email || '').split('@')[0],
        email: r.user_email || r.email || '',
        role: r.role as Member['role'],
        avatar: (r.user_name || r.name || r.user_email || r.email || '?').charAt(0).toUpperCase(),
        joined_at: r.created_at || new Date().toISOString(),
        status: 'active' as const,
      })));
    }).catch(() => {});
  };
  useEffect(loadMembers, [orgId]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Member['role']>('recruiter');
  const [showInvite, setShowInvite] = useState(false);
  const [sendInviteEmail, setSendInviteEmail] = useState(true);
  const [passwordMember, setPasswordMember] = useState<Member | null>(null);
  const [sending, setSending] = useState(false);

  const [editMember, setEditMember] = useState<Member | null>(null);
  const [deleteMember, setDeleteMember] = useState<Member | null>(null);

  const { toasts, show } = useToasts();

  const stats = useMemo(() => {
    return {
      total: members.length,
      admins: members.filter((m) => ['super_admin', 'org_admin'].includes(m.role)).length,
      active: members.filter((m) => m.status === 'active').length,
      pending: members.filter((m) => m.status === 'pending').length,
    };
  }, [members]);

  const filtered = useMemo(() => {
    return members.filter((m) => {
      const matchesSearch =
        m.name.toLowerCase().includes(search.toLowerCase()) ||
        m.email.toLowerCase().includes(search.toLowerCase());
      const matchesRole = roleFilter === 'all' || m.role === roleFilter;
      const matchesStatus = statusFilter === 'all' || m.status === statusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [members, search, roleFilter, statusFilter]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    const name = window.prompt('Имя нового участника:', inviteEmail.split('@')[0]) || inviteEmail.split('@')[0];
    const password = window.prompt('Пароль для нового участника:', '');
    if (!password) return;
    setSending(true);
    try {
      const created = await api.post<{ email_sent: boolean | null }>(`/organizations/${ORG_ID()}/invite`, {
        email: inviteEmail.trim(),
        name,
        password,
        role: inviteRole,
        send_email: sendInviteEmail,
      });
      loadMembers();
      setInviteEmail('');
      setShowInvite(false);
      if (created.email_sent === true) {
        show(`Участник создан, письмо с доступами отправлено на ${inviteEmail}`, 'success');
      } else if (created.email_sent === false) {
        show(`Участник ${inviteEmail} создан, но письмо не отправлено — передайте доступы вручную`, 'error');
      } else {
        show(`Участник ${inviteEmail} создан — сообщите ему доступы лично`, 'success');
      }
    } catch (err) {
      show((err as Error).message || 'Ошибка приглашения', 'error');
    }
    setSending(false);
  };

  const handleSaveRole = async (memberId: string, newRole: Member['role']) => {
    try {
      await api.put(`/organizations/${ORG_ID()}/members/${memberId}`, { role: newRole });
      setMembers((prev) => prev.map((m) => (m.id === memberId ? { ...m, role: newRole } : m)));
      show('Роль обновлена', 'success');
    } catch (err) {
      show((err as Error).message || 'Ошибка', 'error');
    }
    setEditMember(null);
  };

  const handleResendInvite = async (member: Member) => {
    const ok = window.confirm(
      `Участнику ${member.email} будет сгенерирован НОВЫЙ пароль и отправлено письмо с доступами и ссылкой на вход. Старый пароль перестанет работать. Продолжить?`
    );
    if (!ok) return;
    try {
      const res = await api.post<{ email_sent: boolean | null }>(
        `/organizations/${ORG_ID()}/members/${member.id}/resend-invite`
      );
      if (res.email_sent) {
        show(`Письмо с новыми доступами отправлено на ${member.email}`, 'success');
      } else {
        show('Пароль сброшен, но письмо не отправлено — email-сервис не настроен', 'error');
      }
    } catch (err) {
      show((err as Error).message || 'Ошибка отправки приглашения', 'error');
    }
  };

  const handleDelete = async (memberId: string) => {
    try {
      await api.del(`/organizations/${ORG_ID()}/members/${memberId}`);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      show('Участник удалён', 'success');
    } catch (err) {
      show((err as Error).message || 'Ошибка', 'error');
    }
    setDeleteMember(null);
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const roleFilterLabel: Record<RoleFilter, string> = {
    all: 'Все роли',
    org_admin: 'Администратор',
    hr_manager: 'Заказчик',
    recruiter: 'Рекрутер',
    viewer: 'Наблюдатель',
  };

  const statusFilterLabel: Record<StatusFilter, string> = {
    all: 'Все статусы',
    active: 'Активные',
    pending: 'Ожидающие',
  };

  return (
    <div className="space-y-6">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[60] space-y-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`px-4 py-3 rounded-xl shadow-sm border text-sm flex items-center gap-2 animate-[fadeIn_0.2s_ease-out] ${
              t.type === 'success'
                ? 'bg-white border-primary-200 text-primary-800'
                : 'bg-white border-red-200 text-red-700'
            }`}
          >
            <i className={t.type === 'success' ? 'ri-check-line text-primary-500' : 'ri-error-warning-line text-red-500'} />
            {t.message}
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
            Участники команды
          </h1>
          <p className="text-sm text-foreground-500 mt-1">
            Управление доступом и ролями в организации
          </p>
        </div>
        <button
          onClick={() => setShowInvite(!showInvite)}
          className="text-xs flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-user-add-line" />
          Пригласить
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-background-200 p-4">
          <p className="text-xs text-foreground-500">Всего участников</p>
          <p className="text-2xl font-bold text-foreground-900 mt-1">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-4">
          <p className="text-xs text-foreground-500">Администраторы</p>
          <p className="text-2xl font-bold text-accent-600 mt-1">{stats.admins}</p>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-4">
          <p className="text-xs text-foreground-500">Активные</p>
          <p className="text-2xl font-bold text-primary-600 mt-1">{stats.active}</p>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-4">
          <p className="text-xs text-foreground-500">Ожидают приглашения</p>
          <p className="text-2xl font-bold text-secondary-600 mt-1">{stats.pending}</p>
        </div>
      </div>

      {/* Invite form */}
      {showInvite && (
        <div className="bg-white rounded-xl border border-background-200 p-5">
          <h3 className="text-sm font-semibold text-foreground-900 mb-3">Пригласить участника</h3>
          <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="email@company.com"
              required
              className="flex-1 px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as Member['role'])}
              className="px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="org_admin">Администратор</option>
              <option value="hr_manager">Заказчик</option>
              <option value="recruiter">Рекрутер</option>
              <option value="viewer">Наблюдатель</option>
            </select>
            <button
              type="submit"
              disabled={sending}
              className="px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
            >
              {sending ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-send-plane-line" />}
              Отправить
            </button>
          </form>
          <label className="flex items-center gap-2 mt-3 text-sm text-foreground-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={sendInviteEmail}
              onChange={(e) => setSendInviteEmail(e.target.checked)}
              className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
            />
            Отправить участнику письмо со ссылкой, доступами и краткой инструкцией
          </label>
        </div>
      )}

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по имени или email..."
            className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
            className="px-3 py-2.5 rounded-xl bg-white border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
          >
            {ALL_ROLES.map((r) => (
              <option key={r} value={r}>{roleFilterLabel[r]}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="px-3 py-2.5 rounded-xl bg-white border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
          >
            {ALL_STATUSES.map((s) => (
              <option key={s} value={s}>{statusFilterLabel[s]}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Members table */}
      <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-50">
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">Участник</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">Роль</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">Статус</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">Присоединился</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-foreground-500"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-sm text-foreground-400">
                    <i className="ri-user-search-line text-2xl mb-2 block text-foreground-300" />
                    Участники не найдены
                  </td>
                </tr>
              ) : (
                filtered.map((m) => (
                  <tr key={m.id} className="border-b border-background-100 last:border-0 hover:bg-background-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-700 font-heading font-semibold text-sm">{m.avatar}</span>
                        </div>
                        <div>
                          <p className="font-medium text-foreground-900">{m.name}</p>
                          <p className="text-xs text-foreground-400">{m.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full ${roleColors[m.role]}`}>
                        {roleLabels[m.role] || m.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {m.status === 'active' ? (
                        <span className="text-xs text-primary-700 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                          Активен
                        </span>
                      ) : (
                        <span className="text-xs text-accent-700 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                          Ожидает
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground-500 whitespace-nowrap">
                      {formatDate(m.joined_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <MemberActionsMenu
                        member={m}
                        onEditRole={setEditMember}
                        onSetPassword={setPasswordMember}
                        onResendInvite={handleResendInvite}
                        onRemove={setDeleteMember}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-background-100 text-xs text-foreground-400 flex justify-between items-center">
            <span>
              Показано {filtered.length} из {members.length} участников
            </span>
            {(search || roleFilter !== 'all' || statusFilter !== 'all') && (
              <button
                onClick={() => {
                  setSearch('');
                  setRoleFilter('all');
                  setStatusFilter('all');
                }}
                className="text-primary-600 hover:text-primary-700 cursor-pointer transition-colors"
              >
                Сбросить фильтры
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <EditRoleModal
        member={editMember}
        onClose={() => setEditMember(null)}
        onSave={handleSaveRole}
      />
      <DeleteMemberModal
        member={deleteMember}
        onClose={() => setDeleteMember(null)}
        onConfirm={handleDelete}
      />

      {passwordMember && (
        <SetPasswordModal
          member={passwordMember}
          onClose={() => setPasswordMember(null)}
          onDone={(message, type) => {
            setPasswordMember(null);
            show(message, type);
          }}
        />
      )}
    </div>
  );
}
